import ky, { type HTTPError, type KyInstance } from "ky";

import { env } from "@/lib/env";
import { useAuthStore } from "@/stores/authStore";

export interface ApiErrorBody {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: Record<string, unknown> | undefined;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message || `API error ${status}`);
    this.name = "ApiError";
    this.code = body.code;
    this.status = status;
    this.details = body.details;
  }
}

function buildReturnTo(): string {
  if (typeof window === "undefined") return "/";
  const path = window.location.pathname;
  const search = window.location.search;
  return encodeURIComponent(`${path}${search}`);
}

export const apiClient: KyInstance = ky.create({
  prefixUrl: `${env.apiBaseUrl}/api`,
  retry: {
    limit: 2,
    methods: ["get", "post", "patch", "put", "delete"],
    statusCodes: [],
  },
  timeout: 15_000,
  hooks: {
    beforeRequest: [
      (request) => {
        const { token, anonymousId } = useAuthStore.getState();
        if (token) {
          request.headers.set("Authorization", `Bearer ${token}`);
        } else if (anonymousId) {
          request.headers.set("X-Anonymous-Id", anonymousId);
        }
      },
    ],
    beforeError: [
      async (error) => {
        const { response } = error;
        if (response && response.body) {
          try {
            const data = (await response.clone().json()) as { error?: ApiErrorBody };
            if (data.error) {
              return new ApiError(response.status, data.error) as unknown as HTTPError;
            }
          } catch {
            // body wasn't JSON; fall through
          }
        }
        return error;
      },
    ],
    afterResponse: [
      (_request, _options, response) => {
        if (response.status === 401) {
          useAuthStore.getState().clearAuth();
          if (typeof window !== "undefined" && window.location.pathname.startsWith("/app")) {
            window.location.replace(`/login?return_to=${buildReturnTo()}`);
          }
        }
        return response;
      },
    ],
  },
});

export async function apiGet<T>(path: string, searchParams?: Record<string, string>): Promise<T> {
  return apiClient.get(path, { searchParams }).json<T>();
}

export async function apiPost<T>(path: string, json?: unknown): Promise<T> {
  return apiClient.post(path, { json }).json<T>();
}

export async function apiPatch<T>(path: string, json?: unknown): Promise<T> {
  return apiClient.patch(path, { json }).json<T>();
}

export async function apiDelete<T>(path: string): Promise<T> {
  return apiClient.delete(path).json<T>();
}
